<i18n locale="en">
{
  "no-data-title": "No menu items found"
}
</i18n>
<template>
  <OwnStack class="menu-resource-selector" spacing="3">
    <OwnInputContainer :label="label">
      <SearchInput
        v-model="resourceSearchQuery"
        class="w-full"
        standard
        :placeholder="placeholder"
      />
    </OwnInputContainer>
    <OwnSkeletonLoader height="150">
      <OwnStack v-if="!isLoading && resources.length" spacing="3" shrink>
        <SelectorCard
          v-for="resource in resources"
          :key="resource.id"
          :selected="resource.id === resourceId"
          @click="onClick(resource.id)"
        >
          <!--
            @slot menu-item
              @binding {object} resource An individual menu item record
            -->
          <slot name="menu-item" :menu-item="resource"></slot>
        </SelectorCard>
      </OwnStack>
      <!--
        @slot no-data Render a component when no resources are found
        -->
      <slot v-else-if="!isLoading" name="no-data">
        <NoDataCard :title="$t('no-data-title')" />
      </slot>
    </OwnSkeletonLoader>
  </OwnStack>
</template>

<script>
import { SearchInput } from '@/components/inputs'

import { OwnInputContainer, OwnSkeletonLoader, OwnStack } from '@/ui'

import { mapModel } from '@/utils/computed'

import NoDataCard from './NoDataCard.vue'
import SelectorCard from './SelectorCard.vue'

export default {
  name: 'MenuResourceSelector',
  components: {
    NoDataCard,
    OwnInputContainer,
    OwnSkeletonLoader,
    OwnStack,
    SearchInput,
    SelectorCard,
  },
  props: {
    /** Whether to allow the user to deselect an item by clicking */
    allowDeselect: { type: Boolean, default: false },

    /** A function that accepts a query and returns a paginated list of menu items */
    fetchHandler: { type: Function, required: true },

    /** The label for the input container */
    label: { type: String, required: true },

    /** A limit for the number of menu items returned */
    limit: { type: [String, Number], default: 3 },

    /** The ID of the location to search under */
    location: { type: [String, null], default: null },

    /** Placeholder for the search field */
    placeholder: { type: String, required: true },

    /**
     * @model
     *
     * The Item ID
     */
    value: { type: String, default: null },
  },
  data() {
    return {
      isLoading: true,
      resourceSearchQuery: '',
      resources: [],
    }
  },
  computed: {
    ...mapModel('resourceId'),
  },
  watch: {
    location: 'fetchResources',
    resourceSearchQuery: 'fetchResources',
  },
  async mounted() {
    await this.fetchResources()
  },
  methods: {
    async fetchResources() {
      const { fetchHandler, limit, location, resourceSearchQuery } = this
      try {
        this.isLoading = true

        const filter = { limit, locationId: location, page: 1 }
        if (resourceSearchQuery) filter.query = resourceSearchQuery

        const { results } = await fetchHandler(filter)

        this.resources = results
      } finally {
        this.isLoading = false
      }
    },
    onClick(resourceId) {
      const { allowDeselect } = this

      if (this.resourceId === resourceId && allowDeselect) {
        this.resourceId = null
      } else {
        this.resourceId = resourceId
      }
    },
  },
}
</script>
